import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { useCart } from '../context/CartContext';
import {
  getProductById,
  getProductCategory,
  getSuggestionsForProduct,
  getSuggestedCombos,
  SIZES_APPAREL,
  SIZES_SHOES,
  SIZES_SUPPLEMENT,
} from '../data/shopProducts';
import type { ShopItem, Combo } from '../data/shopProducts';

const MIN_QTY = 1;
const MAX_QTY = 9;

function ProductCardSmall({ item }: { item: ShopItem }) {
  const hasPrice = item.price !== '—';
  return (
    <Link to={`/shop/product/${item.id}`} className="block group">
      <article>
        <div className="vyera-img-card vyera-img-card--hover mb-2 relative aspect-[3/4]">
          <img src={item.image} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
        </div>
        <p className="vyera-label text-[10px] text-[var(--vyera-text-secondary)] mb-0.5">{item.detail}</p>
        <h3 className="font-medium text-sm text-[var(--vyera-text-primary)] group-hover:underline">{item.name}</h3>
        {hasPrice && <p className="text-xs text-[var(--vyera-text-secondary)]">{item.price}</p>}
      </article>
    </Link>
  );
}

function ComboCard({
  combo,
  products,
  onAddCombo,
}: {
  combo: Combo;
  products: ShopItem[];
  onAddCombo: (items: ShopItem[]) => void;
}) {
  if (products.length === 0) return null;
  return (
    <article className="border border-[var(--vyera-warm-grey)]/30 rounded-lg overflow-hidden bg-[var(--vyera-cream)]">
      <div className="p-4 flex gap-3">
        <div className="flex -space-x-2">
          {products.slice(0, 3).map((p) => (
            <Link
              key={p.id}
              to={`/shop/product/${p.id}`}
              className="w-14 h-14 rounded-full overflow-hidden border-2 border-[var(--vyera-cream)] shadow shrink-0"
            >
              <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
            </Link>
          ))}
        </div>
        <div className="flex-1 min-w-0">
          {combo.badge && (
            <span className="text-[10px] uppercase tracking-wider text-[var(--vyera-burgundy)] font-medium">
              {combo.badge}
            </span>
          )}
          <h3 className="font-medium text-[var(--vyera-text-primary)]">{combo.name}</h3>
          {combo.description && (
            <p className="text-xs text-[var(--vyera-text-secondary)] mt-0.5">{combo.description}</p>
          )}
          <button
            type="button"
            onClick={() => onAddCombo(products)}
            className="mt-2 text-sm font-medium text-[var(--vyera-burgundy)] hover:underline"
          >
            Add combo to bag
          </button>
        </div>
      </div>
    </article>
  );
}

export default function ProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();
  const product = productId ? getProductById(productId) : undefined;

  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);

  if (!productId || !product) {
    navigate('/shop', { replace: true });
    return null;
  }

  const category = getProductCategory(product.id);
  const sizes = category === 'apparel' ? SIZES_APPAREL : category === 'shoes' ? SIZES_SHOES : SIZES_SUPPLEMENT;
  const hasPrice = product.price !== '—';
  const showSizes = hasPrice && sizes.length > 0;
  const defaultSize = category === 'supplements' && sizes[0] ? sizes[0] : '';
  const effectiveSize = selectedSize || (showSizes && defaultSize ? defaultSize : '');

  const suggestions = getSuggestionsForProduct(product.id, 4);
  const combos = getSuggestedCombos(product.id, 3);

  const handleAddToBag = () => {
    if (!hasPrice) return;
    const size = showSizes ? (selectedSize || effectiveSize) : undefined;
    if (showSizes && !size) return;

    // Aseguramos que el precio sea string para el CartContext
    const priceStr = typeof product.price === 'number' ? `$${product.price}` : product.price;
    const item = { id: product.id, name: product.name, price: priceStr, image: product.image };

    for (let i = 0; i < quantity; i++) addItem(item, 1, size || undefined);
    openCart();
  };

  const handleAddCombo = (items: ShopItem[]) => {
    items.forEach((p) => {
      if (p.price !== '—') {
        const priceStr = typeof p.price === 'number' ? `$${p.price}` : p.price;
        addItem({ id: p.id, name: p.name, price: priceStr, image: p.image }, 1);
      }
    });
    openCart();
  };

  const comboProducts = combos.map((c) => ({
    combo: c,
    products: c.productIds.map((id) => getProductById(id)).filter((p): p is ShopItem => !!p),
  }));

  return (
    <div className="min-h-screen bg-[var(--vyera-cream)]">
      <Navigation />
      <main className="pb-20">
        {/* Breadcrumb */}
        <div className="px-[6vw] pt-6 pb-2">
          <nav className="text-sm text-[var(--vyera-text-secondary)]" aria-label="Breadcrumb">
            <Link to="/shop" className="hover:text-[var(--vyera-burgundy)] transition-colors">
              Shop
            </Link>
            <span className="mx-2">/</span>
            <span className="text-[var(--vyera-text-primary)]">{product.name}</span>
          </nav>
        </div>

        {/* Product block */}
        <section className="px-[6vw] max-w-[1200px] mx-auto py-8 md:py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
            <div className="aspect-[4/5] md:sticky md:top-24 h-max">
              <div className="vyera-img-card w-full h-full">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <p className="vyera-label text-[var(--vyera-text-secondary)] mb-1 tracking-[0.15em]">
                {product.detail}
              </p>
              <h1 className="vyera-heading-lg text-[var(--vyera-text-primary)] mb-3">
                {product.name}
              </h1>
              {hasPrice && (
                <p className="text-xl text-[var(--vyera-text-secondary)] mb-6">{product.price}</p>
              )}
              {product.description && (
                <p className="vyera-body text-[var(--vyera-text-secondary)] mb-8">
                  {product.description}
                </p>
              )}

              {showSizes && (
                <div className="mb-6">
                  <p className="vyera-label text-[var(--vyera-text-primary)] mb-3">Size</p>
                  <div className="flex flex-wrap gap-2">
                    {sizes.map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => setSelectedSize(size)}
                        className={`min-w-[44px] min-h-[44px] px-4 rounded border text-sm font-medium transition-colors ${selectedSize === size
                            ? 'bg-[var(--vyera-black)] text-[var(--vyera-cream)] border-[var(--vyera-black)]'
                            : 'border-[var(--vyera-warm-grey)]/50 text-[var(--vyera-text-primary)] hover:border-[var(--vyera-burgundy)]'
                          }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {hasPrice && (
                <div className="mb-6">
                  <p className="vyera-label text-[var(--vyera-text-primary)] mb-3">Quantity</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.max(MIN_QTY, q - 1))}
                      className="w-10 h-10 rounded border border-[var(--vyera-warm-grey)]/50 text-[var(--vyera-text-primary)] hover:border-[var(--vyera-burgundy)] transition-colors"
                    >
                      −
                    </button>
                    <span className="w-8 text-center font-medium text-[var(--vyera-text-primary)]">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuantity((q) => Math.min(MAX_QTY, q + 1))}
                      className="w-10 h-10 rounded border border-[var(--vyera-warm-grey)]/50 text-[var(--vyera-text-primary)] hover:border-[var(--vyera-burgundy)] transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              {hasPrice ? (
                <button
                  type="button"
                  onClick={handleAddToBag}
                  disabled={showSizes && !selectedSize && !effectiveSize}
                  className="w-full py-4 bg-[var(--vyera-black)] text-[var(--vyera-cream)] font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                  {showSizes && !selectedSize && !effectiveSize ? 'Select size' : 'Add to bag'}
                </button>
              ) : (
                <p className="vyera-body text-[var(--vyera-text-secondary)]">Contact us for availability.</p>
              )}
            </div>
          </div>
        </section>

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <section className="border-t border-[var(--vyera-warm-grey)]/20 py-12 md:py-16">
            <div className="px-[6vw] max-w-[1400px] mx-auto">
              <h2 className="vyera-heading-md text-[var(--vyera-text-primary)] mb-8">
                You might also like
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
                {suggestions.map((item) => (
                  <ProductCardSmall key={item.id} item={item} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Combos */}
        {comboProducts.length > 0 && (
          <section className="border-t border-[var(--vyera-warm-grey)]/20 py-12 md:py-16 bg-[var(--vyera-cream)]">
            <div className="px-[6vw] max-w-[1400px] mx-auto">
              <h2 className="vyera-heading-md text-[var(--vyera-text-primary)] mb-2">
                Combos & sets
              </h2>
              <p className="vyera-body text-[var(--vyera-text-secondary)] mb-8 max-w-lg">
                Complete your look with these curated combinations.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {comboProducts.map(({ combo, products }) => (
                  <ComboCard
                    key={combo.id}
                    combo={combo}
                    products={products}
                    onAddCombo={handleAddCombo}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
